import React, { useState, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, addMonths, subMonths, isToday, parse } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CalendarSidebar = ({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onRefresh,
  onScheduleClick,
  loading = false,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    
    // Add padding days for the start of the week
    const startDay = start.getDay();
    const paddingStart = Array(startDay).fill(null);
    
    return [...paddingStart, ...days];
  }, [currentMonth]);

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      const formatted = format(date, 'yyyy-MM-dd');
      onDateFromChange?.(formatted);
      onDateToChange?.(formatted);
    }
  };

  // Format date for display (e.g., "Jun 4-2025")
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return format(date, 'MMM d-yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="w-[21%] flex-shrink-0 bg-gray-50 h-full flex flex-col overflow-hidden p-4">
      {/* Main Container with white background and rounded corners */}
      <div className="bg-white rounded-xl shadow-md border-2 border-gray-200 flex p-1 flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <h2 className="text-xl font-bold text-gray-800" style={{ fontFamily: 'sans-serif' }}>Manage Appointments</h2>
        </div>

        {/* Date Range Inputs */}
        <div className="px-6 pb-5">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">From</label>
              <div className="relative">
                <input
                  type="date"
                  value={dateFrom || ''}
                  onChange={(e) => onDateFromChange?.(e.target.value)}
                  className="w-full px-3 py-2 text-sm font-medium border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] bg-white transition-all"
                />
              </div>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">To</label>
              <div className="relative">
                <input
                  type="date"
                  value={dateTo || ''}
                  onChange={(e) => onDateToChange?.(e.target.value)}
                  className="w-full px-3 py-2 text-sm font-medium border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] bg-white transition-all"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Calendar Section */}
        <div className="px-6 pb-5">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-md font-semibold text-gray-800">
              {format(currentMonth, 'MMMM yyyy')}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevMonth}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors border-2 border-gray-200 hover:border-[#2D869C]"
              >
                <ChevronLeft size={16} className="text-gray-600" />
              </button>
              <button
                onClick={handleNextMonth}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors border-2 border-gray-200 hover:border-[#2D869C]"
              >
                <ChevronRight size={16} className="text-gray-600" />
              </button>
            </div>
          </div>

          {/* Week Days Header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day, idx) => (
              <div
                key={idx}
                className="text-center text-xs font-semibold text-gray-600 py-1"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, idx) => {
              const isSelected = day && isSameDay(day, selectedDate);
              const isCurrentDay = day && isToday(day);
              const isInRange = day && dateFrom && dateTo && 
                day >= new Date(dateFrom + 'T00:00:00') && 
                day <= new Date(dateTo + 'T00:00:00');
              
              return (
                <button
                  key={idx}
                  onClick={() => handleDateSelect(day)}
                  disabled={!day}
                  className={`
                    aspect-square flex items-center justify-center text-sm rounded-lg transition-all font-medium
                    ${!day ? 'invisible' : ''}
                    ${isSelected 
                      ? 'bg-[#2D869C] text-white font-semibold shadow-sm' 
                      : isInRange
                        ? 'bg-[#9DC9D4]/40 text-[#2D869C] font-semibold'
                        : isCurrentDay
                          ? 'ring-2 ring-[#2D869C] text-[#2D869C] font-semibold'
                          : isSameMonth(day, currentMonth)
                            ? 'text-gray-700 hover:bg-gray-100 hover:text-[#2D869C]'
                            : 'text-gray-300'
                    }
                  `}
                >
                  {day ? format(day, 'd') : ''}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Date Display */}
        <div className="px-6 pb-5">
          <p className="text-sm font-semibold text-gray-700">
            {format(selectedDate, 'EEE, d MMMM yyyy')}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onRefresh}
            disabled={loading}
            className="flex-1 py-3 text-md font-semibold text-white bg-[#2D869C] hover:bg-[#236b7a] rounded-lg transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button
            onClick={onScheduleClick}
            className="flex-1 py-3 text-md font-semibold text-white bg-[#2D869C] hover:bg-[#236b7a] rounded-lg transition-all shadow-sm hover:shadow-md"
          >
            Schedule
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalendarSidebar;
